/*
 * What a child is told about a locked section, composed in ONE place.
 *
 * The server sends the explanation as two structured halves -- `lock_reason`
 * and `lock_hint` (App\Services\Progression in ../wicked-maths-laravel) --
 * because a teacher lock and a rule lock word both halves differently. For the
 * rule lock they are two halves of a single sentence the owner dictated:
 *
 *   "Want access to this section? Earn it. Access is granted based on effort
 *    and achievement at GE, not 'feelings'."
 *
 * Two screens show it: the lesson list greys the group in place, and the
 * questions page answers a stale bookmark that 403s. They MUST read the same,
 * and they used to disagree -- the list joined both halves while the bookmark
 * path showed only the reason, so a child following an old link was challenged
 * without being told the terms. Composing here is what stops those two drifting
 * again.
 *
 * Either half may be absent (a teacher lock with no hint, an older payload), so
 * this joins what it has and returns null when it has nothing, letting the
 * caller fall back to its own generic wording rather than rendering "null".
 */
export const composeLockMessage = (reason, hint) => {
	// Each half is TRIMMED before joining, matching what the server already does
	// to compose the same sentence (`trim($reason.' '.$hint)`). Leaving the
	// client looser than the server would be a latent inconsistency in the one
	// place both ends compose the same words, and it means a later edit adding a
	// trailing space to a constant cannot put a double space in front of a child.
	const parts = [reason, hint]
		.filter(part => typeof part === "string")
		.map(part => part.trim())
		.filter(part => part !== "")

	return parts.length ? parts.join(" ") : null
}

/*
 * One lesson tile's lock (feat/per-student-lesson-locks, ../wicked-maths-laravel
 * docs/PROGRESSION_LOCKDOWN.md §21), for the module page's grouped lessons and
 * its loose tiles alike.
 *
 *  - `locked` is the lesson's own answer whenever the server sends one, so a
 *    lesson she locked inside an open group is shut, and one she unlocked
 *    inside a locked group is open. With none (an older payload), the lesson
 *    follows its group, as it always did.
 *  - A lesson locked ON ITS OWN also carries `lock_kind`, `lock_reason` and
 *    `lock_hint`, and says why on its tile. One locked WITH ITS GROUP carries
 *    `locked` alone: the group's heading already says why, so the tile says
 *    nothing more.
 *  - Locked for everyone: the padlock alone, never words.
 */
export const lessonLockOf = (item, groupLocked = false) => {
	const locked = typeof item.locked === "boolean" ? item.locked : Boolean(groupLocked)
	const message =
		locked && item.lock_kind && item.lock_kind !== "everyone"
			? composeLockMessage(item.lock_reason, item.lock_hint)
			: null
	return { locked, message }
}

export default composeLockMessage
